import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";

/**
 * The activity is already saved by the time this renders in any state: the
 * save request and the check are two separate calls, and the check can fail
 * or run out of time without that ever meaning the report was lost. Every
 * message below starts by saying so, in those words, so a bad outcome here
 * can never read as "your work is gone".
 *
 * That invariant is not local to this file: it holds only as long as the
 * caller (index.jsx) never feeds `checking`/`result` for an activity other
 * than the one currently open. index.jsx enforces this with a poll token
 * that voids a check's answer once its editor has been closed or replaced.
 */
const SAVED_PREFIX = "Attività salvata.";

/** Matches the `kind` enum of the `activity-check` prompt's output schema. */
const KIND_LABELS = {
  missing: "Manca qualcosa",
  format: "Formato",
  open_item: "Impegno aperto",
};

/**
 * Distinct MUI colors per kind, none of them "error": these are suggestions
 * on an already-saved report, not failures, and the palette has to say that
 * at a glance before anyone reads a single sentence.
 */
const KIND_COLORS = {
  missing: "warning",
  format: "info",
  open_item: "secondary",
};

function SuggestionItem({ suggestion }) {
  const kind = suggestion?.kind;
  return (
    <Box component="li" data-testid={`suggestion-kind-${kind}`} sx={{ listStyle: "none" }}>
      <Chip
        color={KIND_COLORS[kind] || "default"}
        // A kind outside the schema's enum still gets a real label instead
        // of the raw, un-translated value landing on screen.
        label={KIND_LABELS[kind] || "Suggerimento"}
        size="small"
        sx={{ mr: 1, mb: 0.5 }}
      />
      <Typography component="span" variant="body2">
        {suggestion?.message}
      </Typography>
    </Box>
  );
}

/**
 * Renders one of the five outcomes `index.jsx` computes after a save
 * (suggestions, complete, incomplete, failed, timeout), plus the wait
 * itself. Never renders anything beyond `result.outcome` and
 * `result.suggestions`: the backend already omits technical detail on a
 * failed or timed-out check
 * (controller-employee-activities.ts) on purpose, and this component must
 * not become a second way for that detail to reach the screen.
 */
export default function CheckSuggestions({ checking = false, result = null }) {
  if (checking) {
    return (
      <Alert data-testid="check-pending" icon={<CircularProgress size={16} />} severity="info">
        {SAVED_PREFIX} Il resoconto è in esame automatico. Puoi chiudere il form: il controllo
        prosegue comunque.
      </Alert>
    );
  }

  if (!result) return null;

  if (result.outcome === "complete") {
    return (
      <Alert data-testid="check-complete" severity="success">
        {SAVED_PREFIX} Il resoconto è completo, nessun suggerimento.
      </Alert>
    );
  }

  if (result.outcome === "failed") {
    return (
      <Alert data-testid="check-failed" severity="warning">
        {SAVED_PREFIX} Il controllo automatico non è riuscito.
      </Alert>
    );
  }

  if (result.outcome === "timeout") {
    return (
      <Alert data-testid="check-timeout" severity="warning">
        {SAVED_PREFIX} Il controllo automatico non ha fatto in tempo a finire.
      </Alert>
    );
  }

  if (result.outcome === "incomplete") {
    return (
      <Alert data-testid="check-incomplete" severity="warning">
        {SAVED_PREFIX} Il resoconto sembra incompleto, ma il controllo automatico non è riuscito a
        indicare cosa manca.
      </Alert>
    );
  }

  if (result.outcome === "suggestions") {
    const suggestions = Array.isArray(result.suggestions) ? result.suggestions : [];
    return (
      <Alert data-testid="check-suggestions" severity="info">
        <Typography sx={{ mb: 1 }} variant="body2">
          {SAVED_PREFIX} Alcuni suggerimenti sul resoconto:
        </Typography>
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 0 }}>
          {suggestions.map((suggestion, index) => (
            <SuggestionItem key={`${suggestion.kind}-${index}`} suggestion={suggestion} />
          ))}
        </Stack>
      </Alert>
    );
  }

  return null;
}
